import { DATA_REGIONS, type DataRegion, type RegistrationInfo } from '@lizrd/contracts';

/**
 * Data-residency region routing (docs/tech/07-eu-data-residency-stack.md). Each
 * region is a separate app host + stack; these helpers decide which host a visitor
 * belongs on and persist the choice. The cookie is scoped to the apex domain so it
 * is shared across `app.lizrd.ai` and `eu.app.lizrd.ai`.
 */
const REGION_COOKIE = 'lizrd_region';

export function readRegionCookie(): DataRegion | null {
  const match = document.cookie.match(/(?:^|;\s*)lizrd_region=([^;]+)/);
  const value = match?.[1];
  return value && (DATA_REGIONS as readonly string[]).includes(value)
    ? (value as DataRegion)
    : null;
}

export function writeRegionCookie(region: DataRegion): void {
  const onLizrd = location.hostname === 'lizrd.ai' || location.hostname.endsWith('.lizrd.ai');
  const parts = [`${REGION_COOKIE}=${region}`, 'path=/', 'max-age=31536000', 'samesite=lax'];
  // Share the choice across both regional hosts (apex-scoped). Omitted on localhost,
  // where a domain-scoped cookie would be rejected.
  if (onLizrd) parts.push('domain=.lizrd.ai');
  if (location.protocol === 'https:') parts.push('secure');
  document.cookie = parts.join('; ');
}

/** The region the visitor should land in: saved choice → request geo → this stack. */
export function effectiveRegion(info: RegistrationInfo): DataRegion {
  return readRegionCookie() ?? info.geoRegion ?? info.region;
}

function isOtherOrigin(host: string | undefined): boolean {
  if (!host) return false;
  try {
    return new URL(host).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * The region to auto-route to on an auth page, or null to stay on this stack.
 * Returns a region only when it differs from this stack AND is served by a different
 * origin (so there is somewhere to go — avoids a same-origin redirect loop and the
 * single-host local stack).
 */
export function autoRouteTarget(info: RegistrationInfo): DataRegion | null {
  const target = effectiveRegion(info);
  if (target !== info.region && isOtherOrigin(info.regionHosts?.[target])) return target;
  return null;
}

/**
 * Persist `region` as the choice and, if it is served by a different origin, redirect
 * there (same path + query). Returns true when it redirected; false when the region
 * is the current origin (the single local stack), so the caller can reflect the choice.
 */
export function routeToRegion(info: RegistrationInfo, region: DataRegion): boolean {
  writeRegionCookie(region);
  const host = info.regionHosts?.[region];
  if (isOtherOrigin(host)) {
    window.location.assign(host! + window.location.pathname + window.location.search);
    return true;
  }
  return false;
}
